import React from 'react';
import { LanguageContext } from '../../utilities/LocalizationModule';
import { Panel, Stack, Text, Label, PanelType, PrimaryButton, Spinner, SpinnerSize, Dropdown, IDropdownOption, TooltipHost, IconButton, mergeStyles, TooltipDelay, MessageBar, MessageBarType } from "@fluentui/react"
import { connect, ConnectedProps } from 'react-redux';
import { setLoading, updateUser } from "../../redux/modules/user"
import { RootState } from '../../redux';
import { assignUsersToSubscription, getTenantSubscriptions, revokeUsersFromSubscription } from '../../utilities/helpers/ApiHelper';
import { AssignSubscriptionRequest, ITenantUser, Subscription } from '../../data-structures/interfaces';
import { SubscriptionStatusEnum } from '../../data-structures/enums';
import { tooltipHeadingStyle, tooltipTextStyle } from '../../styles/PanelStyle';
import { getUserLicenseType } from '../../utilities/helpers/UserLicenseTypeHelper';
import { canAssignUsersToSubscription, getUserIdsRequiringSubscriptionAssignment, isServiceProduct, isSubscriptionAssignable } from '../../utilities/helpers/SubscriptionCapacityHelper';

const revokeSubscriptionKey = "revokeUser";

const mapStateToProps = (state: RootState) => {
    return {
        isLoading: state.user.isUserLoading,
        users: state.user.users,
        tenants: state.user.tenants,
        account: state.user.account,
    }
}

const mapDispatchToProps = {
    setLoading,
    updateUser
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface ManageSubscriptionPanelProps extends PropsFromRedux {
    isOpen: boolean,
    closePanel: () => void;
    tenantUser: ITenantUser,
    selectedUsers: { id: number, tenantId: string, tenantName: string, displayName: string }[]
};

const ManageSubscriptionPanel: React.FC<ManageSubscriptionPanelProps> = ({
    isOpen,
    closePanel,
    tenants,
    users,
    tenantUser,
    selectedUsers,
    updateUser
}: ManageSubscriptionPanelProps) => {
    const languageStrings = React.useContext(LanguageContext);
    const [subscriptions, setSubscriptions] = React.useState<Subscription[]>([]);
    const [subscriptionOptions, setSubscriptionOptions] = React.useState<IDropdownOption[]>([]);
    const [selectedSubscription, setSelectedSubscription] = React.useState<IDropdownOption>();

    const [tenantOptions, setTenantOptions] = React.useState<IDropdownOption[]>([]);
    const [selectedTenant, setSelectedTenant] = React.useState<IDropdownOption>();

    const [isSaving, setIsSaving] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState<string>();

    const getNotEnoughLicensesMessage = React.useCallback((needed: number, available: number) => {
        return languageStrings.NotEnoughLicensesAvailable
            .replace("{needed}", needed.toString())
            .replace("{available}", available.toString());
    }, [languageStrings]);

    const getAssignmentValidationError = React.useCallback((subscription: Subscription | undefined, requiredLicenseCount: number): string => {
        if (requiredLicenseCount <= 0) {
            return "";
        }

        if (isServiceProduct(subscription)) {
            return languageStrings.ServiceProductNotAssignable;
        }

        if (!isSubscriptionAssignable(subscription)) {
            return languageStrings.SubscriptionUnavailable;
        }

        if (!canAssignUsersToSubscription(subscription, requiredLicenseCount)) {
            return getNotEnoughLicensesMessage(requiredLicenseCount, subscription?.availableLicenseCount ?? 0);
        }

        return "";
    }, [getNotEnoughLicensesMessage, languageStrings]);

    React.useEffect(() => {
        if (selectedUsers?.length > 0) {
            const uniqueTenants = selectedUsers.reduce((acc, user) => {
                const foundTenant = acc.find(tenant => tenant.tenantId === user.tenantId);

                if (!foundTenant) {
                    acc.push({ tenantId: user.tenantId, tenantName: user.tenantName });
                }

                return acc;
            }, []);

            const dropdownOptions = uniqueTenants.map(tenant => ({
                key: tenant.tenantId,
                text: tenant.tenantName
            }));

            setTenantOptions(dropdownOptions);
        }
    }, [selectedUsers]);

    React.useEffect(() => {
        if (tenantOptions.length > 0 && !selectedTenant) {
            setSelectedTenant(tenantOptions[0])
        }
    }, [tenantOptions, selectedTenant]);

    React.useEffect(() => {
        (async () => {
            if (!selectedTenant) {
                return;
            }

            setSelectedSubscription(undefined);
            const tenant = tenants.find(x => x.tenantId === selectedTenant.key);
            if (!tenant) {
                return;
            }

            const subscriptions = await getTenantSubscriptions(tenant.id, languageStrings);

            setSubscriptions(subscriptions);
            const tenantUsers = selectedUsers.filter(x => x.tenantId === selectedTenant.key);

            const tenantSubs: IDropdownOption[] = subscriptions
                .filter(x => {
                    return isSubscriptionAssignable(x);
                })
                .sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime())
                .map(sub => {
                    let licenseText;
                    const status: string = (sub.status as string)?.toLowerCase();

                    // if (status === SubscriptionStatusEnum.Canceled) {
                    //     licenseText = `(${languageStrings.Canceled})`;
                    // } else if (sub.method === "invoice" || sub.method === "invoice_static" || sub.method === "invoice_dynamic") {
                    //     licenseText = `(${languageStrings.Invoice})`;
                    // } else if (sub.tier === SubscriptionTierEnum.Trial) {
                    //     licenseText = `(${new Date(sub.expiryDate).toLocaleDateString()})`;
                    // } else {
                    //     licenseText = `(${sub.availableLicenses} ${languageStrings.Available.toLocaleLowerCase()})`;
                    // }

                    if (isServiceProduct(sub)) {
                        licenseText = `(${languageStrings.ServiceAddOn})`;
                    } else if (
                        status === SubscriptionStatusEnum.Suspended
                        || status === SubscriptionStatusEnum.Completed
                        || (status === SubscriptionStatusEnum.Canceled && (!sub.expiryDateRaw || sub.expiryDateRaw < new Date()))
                    ) {
                        licenseText = `(${languageStrings.Inactive})`;
                    } else {
                        licenseText = `(${sub.availableLicenses !== "-" ? sub.availableLicenses : languageStrings.Unlimited} ${languageStrings.Available.toLocaleLowerCase()})`;
                    }

                    if (sub.interval) {
                        licenseText += sub.interval === languageStrings.OnceOff
                            ? " - Trial"
                            : ` - ${sub.interval}`;
                    }

                    const userIdsRequiringAssignment = getUserIdsRequiringSubscriptionAssignment(
                        tenantUsers.map(x => x.id),
                        sub.id,
                        users
                    );

                    const option: IDropdownOption = {
                        key: sub.id,
                        text: `${sub.product} ${licenseText}`,
                        data: sub,
                        disabled: !canAssignUsersToSubscription(sub, userIdsRequiringAssignment.length)
                    }

                    return option;
                });

            tenantSubs.unshift({
                key: revokeSubscriptionKey,
                text: languageStrings.RevokeLicenses,
            })

            setSubscriptionOptions(tenantSubs);

            if (tenantUsers.length === 1) {
                const tenantUser = users.find(x => x.id === tenantUsers[0].id);

                if (tenantUser?.subscriptionId) {
                    const sub = tenantSubs.find(x => x.key === tenantUser?.subscriptionId)
                    if (sub) {
                        setSelectedSubscription(sub);
                    }
                }
            }
        })();
    }, [languageStrings, selectedTenant, selectedUsers, tenants, users]);

    const onSubscriptionChange = (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
        setErrorMessage(undefined);
        setSelectedSubscription(item);
    };

    const onTenantChange = (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
        setErrorMessage(undefined);
        setSelectedTenant(item);
    };

    const TooltipContent = () => {
        return <>
            <Label styles={tooltipHeadingStyle} style={{ paddingBottom: 0 }}>{languageStrings.Users}</Label>
            <ul className={mergeStyles({ paddingLeft: 25 })}>
                <Stack>
                    {[...selectedUsers.filter(x => x.tenantName === selectedTenant.text)].sort().map((u, i) =>
                        <li key={i}><Label styles={tooltipTextStyle}>
                            {u.displayName}
                        </Label></li>)}
                </Stack>
            </ul>
        </>
    }

    return (
        <Panel
            headerText={languageStrings.ManageSubscription}
            isOpen={isOpen}
            isLightDismiss
            onDismiss={() => {
                closePanel();
            }}
            closeButtonAriaLabel="Close"
            type={PanelType.custom}
            customWidth={"500px"}
        >
            <Stack tokens={{ childrenGap: 20 }} style={{ marginTop: 20 }}>
                {tenantOptions?.length > 0 && <Dropdown
                    label={languageStrings.Tenant}
                    selectedKey={selectedTenant?.key}
                    options={tenantOptions}
                    onChange={onTenantChange}
                />}
                <Stack horizontal verticalAlign='center'>
                    {selectedTenant && <Text>{selectedUsers.filter(x => x.tenantId === selectedTenant.key).length} user{selectedUsers.filter(x => x.tenantId === selectedTenant.key).length === 1 ? '' : 's'} selected from {selectedTenant.text}</Text>}
                    <TooltipHost
                        content={selectedTenant && <TooltipContent />}
                        calloutProps={{ gapSpace: 0, calloutWidth: 200 }}
                        styles={{
                            root: {
                                display: 'inline-block',
                            }
                        }}
                        delay={TooltipDelay.zero}
                    >
                        <IconButton iconProps={{
                            iconName: 'Info'
                        }} />
                    </TooltipHost>
                </Stack>
                {subscriptionOptions?.length > 0 && <Dropdown
                    placeholder={languageStrings.SelectSubscription}
                    options={subscriptionOptions}
                    onChange={onSubscriptionChange}
                    selectedKey={selectedSubscription?.key}
                />}
                {errorMessage && <MessageBar messageBarType={MessageBarType.error}>{errorMessage}</MessageBar>}
                <Stack horizontalAlign='end'>
                    <PrimaryButton
                        disabled={!selectedSubscription || isSaving}
                        text={languageStrings.Save}
                        onClick={async () => {
                            setIsSaving(true);
                            setErrorMessage(undefined);
                            try {
                                const tenant = tenants.find(x => x.tenantId === selectedTenant.key);
                                if (!tenant) {
                                    setErrorMessage(languageStrings.NoTenantSelected);
                                    return;
                                }

                                const gridTenantUsers = selectedUsers.filter(x => x.tenantId === selectedTenant.key);
                                const selectedTenantUserIds = gridTenantUsers.map(x => x.id);

                                const request: AssignSubscriptionRequest = {
                                    tenantId: tenant.id,
                                    subscriptionId: selectedSubscription.key === revokeSubscriptionKey ? null : selectedSubscription.key as number,
                                    userIds: selectedTenantUserIds
                                }

                                let updatedUserIdList: number[];
                                let subscriptionForUpdatedUsers: Subscription | undefined;
                                if (selectedSubscription.key === revokeSubscriptionKey) {
                                    const revokeResult = await revokeUsersFromSubscription(request);
                                    if (revokeResult === null || revokeResult === undefined) {
                                        setErrorMessage(languageStrings.SubscriptionAssignmentFailed);
                                        return;
                                    }
                                    updatedUserIdList = request.userIds;
                                } else {
                                    const subscriptionId = Number(selectedSubscription.key);
                                    if (Number.isNaN(subscriptionId)) {
                                        setErrorMessage(languageStrings.SubscriptionAssignmentFailed);
                                        return;
                                    }

                                    const currentSubscriptions = await getTenantSubscriptions(tenant.id, languageStrings);
                                    setSubscriptions(currentSubscriptions);

                                    const subscription = currentSubscriptions.find(x => x.id === subscriptionId);
                                    subscriptionForUpdatedUsers = subscription;
                                    const userIdsRequiringAssignment = getUserIdsRequiringSubscriptionAssignment(
                                        selectedTenantUserIds,
                                        subscriptionId,
                                        users
                                    );
                                    const validationError = getAssignmentValidationError(subscription, userIdsRequiringAssignment.length);
                                    if (validationError) {
                                        setErrorMessage(validationError);
                                        return;
                                    }

                                    if (userIdsRequiringAssignment.length === 0) {
                                        closePanel();
                                        return;
                                    }

                                    request.userIds = userIdsRequiringAssignment;
                                    updatedUserIdList = await assignUsersToSubscription(request);
                                    const allRequestedUsersWereUpdated = Array.isArray(updatedUserIdList)
                                        && userIdsRequiringAssignment.every(userId => updatedUserIdList.includes(userId));

                                    if (!allRequestedUsersWereUpdated) {
                                        if (Array.isArray(updatedUserIdList) && updatedUserIdList.length > 0) {
                                            const tenantUsers: ITenantUser[] = users.filter(user => updatedUserIdList.includes(user.id));
                                            tenantUsers.forEach(user => {
                                                const newTenantUser = { ...user };
                                                newTenantUser.subscriptionId = subscription.id;
                                                newTenantUser.licenseType = getUserLicenseType(subscription);
                                                newTenantUser.productType = subscription.product?.trim() ?? null;
                                                updateUser(newTenantUser, true);
                                            });
                                        }

                                        setErrorMessage(languageStrings.SubscriptionAssignmentFailed);
                                        return;
                                    }
                                }

                                if (Array.isArray(updatedUserIdList) && updatedUserIdList.length > 0) {
                                    const tenantUsers: ITenantUser[] = users.filter(user => updatedUserIdList.includes(user.id));
                                    const subscription = subscriptionForUpdatedUsers ?? subscriptions.find(x => x.id === request.subscriptionId);
                                    tenantUsers.forEach(user => {
                                        const newTenantUser = { ...user };
                                        if (selectedSubscription.key === revokeSubscriptionKey) {
                                            newTenantUser.subscriptionId = null;
                                            newTenantUser.licenseType = getUserLicenseType(null);
                                            newTenantUser.productType = null;
                                        } else if (subscription) {
                                            newTenantUser.subscriptionId = subscription.id;
                                            newTenantUser.licenseType = getUserLicenseType(subscription);
                                            newTenantUser.productType = subscription.product?.trim() ?? null;
                                        } else {
                                            return;
                                        }
                                        updateUser(newTenantUser, true);
                                    });
                                }
                                closePanel();
                            } catch (error) {
                                console.error(error);
                                setErrorMessage(languageStrings.SubscriptionAssignmentFailed);
                            } finally {
                                setIsSaving(false);
                            }
                        }}
                    >
                        {isSaving && <Spinner size={SpinnerSize.small} style={{ paddingLeft: 10 }} />}
                    </PrimaryButton>
                </Stack>
            </Stack>

        </Panel>
    )
}

export default connector(ManageSubscriptionPanel);
